import React, { useState, useEffect } from 'react';
import { Badge, Popover, List, Empty, Button, Spin, Typography, Avatar } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheckDouble } from '@fortawesome/pro-solid-svg-icons';
import { faBell as faBellRegular } from '@fortawesome/pro-regular-svg-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, Notification } from '../../services/notifications';
import { useAuthStore } from '../../store/authStore';
import { textStyles } from '../../utils/textStyles';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Icon map for different notification types
const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'TASK_ASSIGNED':
      return { icon: '👤', color: '#DB50AC' };
    case 'COMMENT_ADDED':
      return { icon: '💬', color: '#4096ff' };
    case 'TASK_DUE_SOON':
      return { icon: '⏰', color: '#FF9800' };
    case 'TASK_OVERDUE':
      return { icon: '⚠️', color: '#D55C52' };
    case 'TASK_MENTIONED':
      return { icon: '@', color: '#52c41a' };
    case 'TASK_STATUS_CHANGED':
      return { icon: '🔄', color: '#9E67D0' };
    default:
      return { icon: '📋', color: '#BFBFBF' };
  }
};

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setSocket used in useEffect
  const [socket, setSocket] = useState<Socket | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Query for notifications
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => getNotifications({ limit: 20 }),
    refetchInterval: 60000, // Refetch every minute
  });

  // Query for unread count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!user?.id) return;

    const newSocket = io(API_URL, {
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('🔔 Notification socket connected');
      newSocket.emit('join_user', user.id);
    });

    newSocket.on('notification', (notification: Notification) => {
      console.log('🔔 New notification received:', notification);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    newSocket.on('disconnect', () => {
      console.log('🔔 Notification socket disconnected');
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.emit('leave_user', user.id);
        newSocket.disconnect();
      }
    };
  }, [user?.id, queryClient]);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
    if (notification.link) {
      const url = new URL(notification.link, window.location.origin);
      navigate(url.pathname + url.search);
    }
    setOpen(false);
  };

  const content = (
    <div style={{ width: 360, maxHeight: 480 }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #333',
      }}>
        <Text style={{ ...textStyles.bodyBold, color: '#F2F2F2' }}>
          Notifications
        </Text>
        {unreadCount > 0 && (
          <Button 
            type="text" 
            size="small"
            onClick={() => markAllAsReadMutation.mutate()}
            loading={markAllAsReadMutation.isPending}
            style={{ color: '#DB50AC' }}
          >
            <FontAwesomeIcon icon={faCheckDouble} style={{ marginRight: 4 }} />
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div style={{ maxHeight: 400, overflow: 'auto' }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty 
            description="No notifications" 
            style={{ padding: 40 }}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(notification: Notification) => {
              const { icon } = getNotificationIcon(notification.type);
              return (
                <List.Item
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    background: notification.read ? 'transparent' : 'rgba(219, 80, 172, 0.08)',
                    borderBottom: '1px solid #2a2a2a',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.read ? 'transparent' : 'rgba(219, 80, 172, 0.08)';
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        style={{ 
                          background: 'transparent', 
                          fontSize: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {icon}
                      </Avatar>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text style={{ 
                          ...textStyles.calloutBold, 
                          color: notification.read ? '#BFBFBF' : '#F2F2F2',
                        }}>
                          {notification.title}
                        </Text>
                        {!notification.read && (
                          <div style={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            background: '#DB50AC',
                            flexShrink: 0,
                            marginLeft: 8,
                          }} />
                        )}
                      </div>
                    }
                    description={
                      <div>
                        <Text style={{ 
                          ...textStyles.footnoteRegular, 
                          color: '#8C8C8C',
                          display: 'block',
                          marginBottom: 4,
                        }}>
                          {notification.message}
                        </Text>
                        <Text style={{ 
                          ...textStyles.footnoteRegular, 
                          color: '#595959',
                        }}>
                          {dayjs(notification.createdAt).fromNow()}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayStyle={{ padding: 0 }}
      overlayInnerStyle={{ 
        background: '#1a1a1a', 
        borderRadius: 12,
        border: '1px solid #333',
        overflow: 'hidden',
      }}
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={
            <FontAwesomeIcon 
              icon={unreadCount > 0 ? faBell : faBellRegular} 
              style={{ 
                fontSize: 18, 
                color: unreadCount > 0 ? '#DB50AC' : '#BFBFBF',
              }} 
            />
          }
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        />
      </Badge>
    </Popover>
  );
};

export default NotificationBell;
